import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { ITableRowProps, IColumnConfig } from '@/types/admin/dataTable';
import { ADMIN_COLORS } from '@/constants/admin';
import { SelectionIndicator } from '@/components/common/admin/mobile/SelectionIndicator';

export const TableRow = <T,>({ 
  row, 
  columns, 
  index, 
  onPress,
  isTabletView = false,
  mobileColumnCount = 2,
  isSelected = false,
  enableSelection = false
}: ITableRowProps<T>) => {
  
  const renderCellValue = (column: IColumnConfig<T>, value: any) => {
    // On mobile, prefer mobile-specific renderer when available
    if (!isTabletView && column.renderMobileCell) {
      return column.renderMobileCell(value, row, index);
    }

    // Otherwise fall back to generic renderer
    if (column.renderCell) {
      return column.renderCell(value, row, index);
    }
    
    // Default rendering based on type
    if (value === null || value === undefined) {
      return <Text style={styles.cellText}>N/A</Text>;
    }
    
    if (typeof value === 'boolean') {
      return <Text style={styles.cellText}>{value ? 'Yes' : 'No'}</Text>;
    }
    
    if (value instanceof Date) {
      return (
        <Text style={styles.cellText}>
          {value.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </Text>
      );
    }
    
    return <Text style={styles.cellText} numberOfLines={3}>{String(value)}</Text>;
  };

  const visibleColumns = columns.filter(column => {
    if (isTabletView) {
      return column.visible !== false;
    }
    return column.mobileVisible !== false;
  });

  // For mobile: split columns into rows based on mobileColumnCount
  const mobileRows: IColumnConfig<T>[][] = [];
  if (!isTabletView) {
    for (let i = 0; i < visibleColumns.length; i += mobileColumnCount) {
      mobileRows.push(visibleColumns.slice(i, i + mobileColumnCount));
    }
  }

  const renderMobileGrid = () => {
    return mobileRows.map((rowColumns, rowIndex) => (
      <View key={rowIndex} style={styles.mobileRow}>
        {rowColumns.map((column, colIndex) => (
          <View 
            key={String(column.key)} 
            style={[
              styles.mobileCell,
              colIndex === 0 ? styles.mobileCellLeft : styles.mobileCellRight
            ]}
          >
            {renderCellValue(column, row[column.key as keyof T])}
          </View>
        ))}
        {/* Fill remaining space if row is not complete */}
        {rowColumns.length < mobileColumnCount && 
          Array.from({ length: mobileColumnCount - rowColumns.length }).map((_, emptyIndex) => (
            <View 
              key={`empty-${emptyIndex}`} 
              style={[styles.mobileCell, styles.mobileCellRight]} 
            />
          ))
        }
      </View>
    ));
  };

  const renderTabletGrid = () => {
    const primaryColumns = visibleColumns.slice(0, 2);
    const secondaryColumns = visibleColumns.slice(2);

    return (
      <>
        <View style={styles.topRow}>
          {primaryColumns.map((column, colIndex) => (
            <View 
              key={String(column.key)} 
              style={[
                styles.primaryCell,
                colIndex === 0 ? styles.primaryCellLeft : styles.primaryCellRight
              ]}
            >
              {renderCellValue(column, row[column.key as keyof T])}
            </View>
          ))}
        </View>
        
        {/* Additional info for tablet view */}
        {secondaryColumns.length > 0 && (
          <View style={styles.additionalInfo}>
            {secondaryColumns.map((column) => (
              <View key={String(column.key)} style={styles.infoRow}>
                <Text style={styles.infoLabel}>{column.label}:</Text>
                <View style={styles.infoValue}>
                  {renderCellValue(column, row[column.key as keyof T])}
                </View>
              </View>
            ))}
          </View>
        )}
      </>
    );
  };

  return (
    <TouchableOpacity 
      style={[
        styles.container,
        isSelected && styles.selectedContainer
      ]} 
      onPress={() => onPress?.(row, index)}
      activeOpacity={0.7}
    >
      {/* Selection indicator */}
      {enableSelection && (
        <SelectionIndicator selected={isSelected} size={20} />
      )}
      
      <View style={styles.mainContent}>
        {isTabletView ? renderTabletGrid() : renderMobileGrid()}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderBottomWidth: 2,
    borderBottomColor: ADMIN_COLORS.DATA_TABLE.BORDER,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectedContainer: {
  },
  
  mainContent: {
    flex: 1,
  },
  // Mobile grid styles
  mobileRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  mobileCell: {
    flex: 1,
    paddingHorizontal: 4, // Small padding for better spacing
  },
  mobileCellLeft: {
    alignItems: 'flex-start',
  },
  mobileCellRight: {
    alignItems: 'flex-end',
  },
  mobileCellWithMargin: {
    // No margin needed with space-between
  },
  // Tablet styles (original layout)
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  primaryCell: {
    flex: 1,
  },
  primaryCellLeft: {
    marginRight: 12,
  },
  primaryCellRight: {
    alignItems: 'flex-end',
  },
  cellText: {
    fontSize: 14,
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
    flexWrap: 'wrap',
  },
  additionalInfo: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F5F5F5',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  infoLabel: {
    fontSize: 12,
    color: '#757575',
    fontWeight: '500',
  },
  infoValue: {
    flex: 1,
    alignItems: 'flex-end',
  },
});
